import { ReceiveCouponV2Data } from '@/api/coupon';
import { reportCustomMetric } from '@/lib/owl';
import { removeImageScheme } from '@/lib/utils';
import {
  ActivityCouponResult,
  CouponWallTab,
  TitleItem,
  CouponWallItem,
  CouponData,
} from '@/types/coupon-wall';

/**
 * 数据转换：将API返回的数据转换为组件需要的格式
 */
export const transformData = (data: ActivityCouponResult): {
  title: TitleItem[];
  tabs: CouponWallTab[];
  coupons: CouponWallItem[];
  currentTime: number;
  countdownAnimationToggle: boolean;
  showCouponValue: boolean;
  recallToken: string;
  activityCode: string;
  activityName: string;
  activityScene: number;
  quickScreenCouponList: CouponData[];
} => {
  // 获取节日券配置
  const festivalCouponConfig = data.config?.festivalCouponConfig;
  const festivalCouponList = festivalCouponConfig?.festivalCouponList || [];

  const coupons: CouponWallItem[] = (data.list || []).map((item) => {
    // 判断是否为节日券
    const isFestivalCoupon = festivalCouponConfig?.enable && 
      item?.data?.couponConfigId && 
      festivalCouponList.includes(item.data.couponConfigId);

    return {
      ...item,
      data: {
        ...item.data,
        isFestivalCoupon: isFestivalCoupon || false,
      },
    }
  });

  return {
    title: data.title,
    tabs: Array.isArray(data.config?.tabList) && data.config.tabList.length > 0 ? data.config.tabList.map(tab => ({
      ...tab,
      tabIcon: removeImageScheme(tab.tabIcon)
    })) : [],
    coupons,
    currentTime: data.currentTime,
    countdownAnimationToggle: data.config?.couponStyleConfig?.countdownAnimationToggle || false,
    showCouponValue: data.config?.couponStyleConfig?.showPrice || false,
    recallToken: data.recallToken || '',
    activityCode: data.config?.activityCode || '',
    activityName: data.config?.activityName || '',
    activityScene: data.config?.activityScene || 0,
    quickScreenCouponList: data.quickScreenCouponList || [],
  };
};

export const transformDataV2 = (data: ReceiveCouponV2Data): {
  title: TitleItem[];
  tabs: CouponWallTab[];
  coupons: CouponWallItem[];
  currentTime: number;
  countdownAnimationToggle: boolean;
  showCouponValue: boolean;
  recallToken: string;
  activityCode: string;
  activityName: string;
  activityScene: number;
  quickScreenCouponList: CouponData[];
} => {
  // 获取节日券配置
  const festivalCouponConfig = data.config?.festivalCouponConfig;
  const festivalCouponList = festivalCouponConfig?.festivalCouponList || [];

  const coupons: CouponWallItem[] = (data.totalGrantedCouponList || []).map((item) => {
    // 判断是否为节日券
    const isFestivalCoupon = festivalCouponConfig?.enable && 
      item.data?.couponConfigId && 
      festivalCouponList.includes(item.data.couponConfigId);

    return {
      ...item,
      data: {
        ...item.data,
        isFestivalCoupon: isFestivalCoupon || false,
      },
    }
  });

  return {
    title: data.title,
    tabs: Array.isArray(data.config?.tabList) && data.config.tabList.length > 0 ? data.config.tabList.map(tab => ({
      ...tab,
      tabIcon: removeImageScheme(tab.tabIcon)
    })) : [],
    coupons,
    currentTime: data.currentTime,
    countdownAnimationToggle: data.config?.couponStyleConfig?.countdownAnimationToggle || false,
    showCouponValue: data.config?.couponStyleConfig?.showPrice || false,
    recallToken: '',
    activityCode: data.config?.activityCode || '',
    activityName: data.config?.activityName || '',
    activityScene: data.config?.activityScene || 0,
    quickScreenCouponList: data.quickScreenCouponList || [],
  };
};

/**
 * 获取 Tab 的有效 planCode 列表
 * 如果 planCodeList 存在且非空则使用 planCodeList，否则 fallback 到 [planCode]
 */
export const getTabPlanCodes = (tab: CouponWallTab): string[] => {
  return tab.planCodeList?.length ? tab.planCodeList : [tab.planCode];
};

// 通用工具方法

// 浮点数比较工具方法
const EPSILON = 1e-8; // 浮点数比较精度值
// 相等
export const eq = (a: number, b: number) => {
    return Math.abs(a - b) < EPSILON;
}
// 小于
export const lt = (a: number, b: number) => {
    return (a < b) && (!(eq(a, b)));
}
// 小于等于
export const le = (a: number, b: number) => {
    return (a < b) || (eq(a, b));
}
// 大于
export const gt = (a: number, b: number) => {
    return (a > b) && (!(eq(a, b)));
}
// 大于等于
export const ge = (a: number, b: number) => {
    return (a > b) || (eq(a, b));
}

// 业务相关工具方法
export const parsePrice = (priceFen: number): {
    yuan: number,
    fen: number,
    priceText:string
} => {
    let yuan = 0;
    let fen = 0;
    let priceText = "0";
    priceFen = Math.round(priceFen);
    if (gt(priceFen, 0)) {
      yuan = Math.floor(priceFen / 100);
      fen = Math.round(priceFen % 100);
      priceText = (priceFen / 100).toFixed(fen > 0 ? (fen % 10 == 0 ? 1 : 2) : 0);
    }
    return {
        yuan: yuan,
        fen: fen,
        priceText: priceText
    }
}


import { CouponWallRenderStatus } from './constants';

/**
 * 上报红包墙预发券结果状态
 */
export const reportCouponWallDisplayStatus = (displayStatus: CouponWallRenderStatus) => {
  reportCustomMetric({
    key: "couponWallDisplay",
    value: displayStatus === CouponWallRenderStatus.ERROR ? 0 : 1,
    tags: {
      displayStatus,
    },
  });
}

/**
 * 上报红包墙领取状态
 */
type ReceiveErrorMsg = "success" | "unLogin" | "apiFail" | "noData";
export const reportCouponWallReceiveSuccessRate = (isSuccess: boolean, errMsg: ReceiveErrorMsg = "success") => {
  reportCustomMetric({
    key: "couponWallReceive",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
    },
  });
}


export enum GoToUseType {
  RecallSupply = "recallSupply", // 供给列表召回
  JumpLink = "jumpLink", // 跳转
  DownloadApp = "downloadApp", // 下载App
  JumpApp = "jumpApp", // 打开App
}
type GoToUseErrorMsg = "success" | "noSegmentIdOrWayneActId" | "noJumpLink" | "jumpFail" | "notSupportedJumpType";
export const reportCouponWallGoToUseSuccessRate = (isSuccess: boolean, goToUseType: GoToUseType, errMsg: GoToUseErrorMsg = "success") => {
  reportCustomMetric({
    key: "couponWallGoToUse",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
      goToUseType,
    },
  });
}
